// models
import { type AxiosInstance } from 'axios';
import type {
  EntryDetailDto,
  EntryHistoryItem,
  EntryHistoryResDto,
  EntryListDto,
  EntryMoCountDtoGroup,
  EntryTypeDto,
  EntryWeeklyDtoEntryWeeklyResDto,
  ReqAttendanceCount,
  ReqEntryBatchInsert,
  ReqEntryHistorySearch,
  ReqEntryHistoryUser,
  ReqEntrySearch,
  ResEntries,
  ResEntryHistoryUser,
  ResEntryStatistics,
  ResEntryWeekly
} from '../models/entry.model';

// services
import AxiosInstanceCreator from 'services/api';
import { BaseApiResponse } from 'modules';
import { ReqEntryHistoryToday, ResEntryHistoryToday } from '../models/history/today.model';
import { ReqEntryHistoryWeekly, ResEntryHistoryWeekly } from '../models/history/weekly.model';
import { ReqEntryHistoryMonthly, ResEntryHistoryMonthly } from '../models/history/monthly.model';

const entryInstance = new AxiosInstanceCreator().create();

export const entryApi = {
  /**
   * 출입 이력 통계
   * @param data
   * @returns {Promise<*>}
   */
  async getDailyStatisticsEntry(data: any) {
    return await entryInstance.get(`/entry/${data.entryDate}`).then((res) => res.data);
  },

  /**
   * 주간 출입 이력 통계
   * @param data
   * @returns {Promise<*>}
   */
  async getWeeklyStatisticsEntry(data: any) {
    return await entryInstance.get(`/entry/${data.entryDate}/weekly`).then((res) => res.data);
  },

  /**
   * 사용자 개인의 출역 이력 페이지 조회 API
   *
   * @param {ReqEntryHistoryUser} data
   * @returns {Promise<ReqEntryHistoryUser>}
   */
  async getEntryHistoryUser(params: ReqEntryHistoryUser): Promise<ResEntryHistoryUser> {
    return await entryInstance
      .get<ResEntryHistoryUser>(`/entry/history/${params.workerId}`, { params })
      .then((res) => res.data);
  },

  /**
   * 출입이력 통계 자료 조회
   * @param {entryDate} data
   * @returns
   */
  async getEntryStatistics(entryDate: string): Promise<ResEntryStatistics> {
    return entryInstance.get(`/entry/${entryDate}`).then((res) => res.data);
  },

  /**
   * 출입이력 주간 통계 자료 조회
   * @param {entryDate} data
   * @returns
   */
  async getEntryStatisticsWeekly(entryDate: string): Promise<ResEntryWeekly> {
    return entryInstance.get(`/entry/${entryDate}/weekly`).then((res) => res.data);
  },

  /**
   * 출입이력 목록 조회
   * @param {entryDate, companyId, workerType, status, keyword, page, size} data
   * @returns
   */
  async getEntryList(query: string): Promise<ResEntries> {
    return entryInstance.get(`/entry?${query}`).then((res) => res.data);
  },

  /**
   * 금일 출입이력 조회
   */
  async getEntryHistoryToday(request: ReqEntryHistoryToday): Promise<ResEntryHistoryToday> {
    return entryInstance.get(`/entry/history/today`, { params: request }).then((res) => res.data);
  },

  /**
   * 주간 출입이력 조회
   */
  async getEntryHistoryWeekly(request: ReqEntryHistoryWeekly): Promise<ResEntryHistoryWeekly> {
    return entryInstance.get(`/entry/history/weekly`, { params: request }).then((res) => res.data);
  },

  /**
   * 월간 출입이력 조회
   */
  async getEntryHistoryMonthly(request: ReqEntryHistoryMonthly): Promise<ResEntryHistoryMonthly> {
    return entryInstance.get(`/entry/history/monthly`, { params: request }).then((res) => res.data);
  }
};

export class EntryAPI {
  Axios: AxiosInstance;
  AxiosV2: AxiosInstance;

  constructor() {
    this.Axios = new AxiosInstanceCreator().create();
    this.AxiosV2 = new AxiosInstanceCreator({
      baseURL: import.meta.env.DEV ? '/v2/api' : `${import.meta.env.VITE_API_URL}/v2/api`
    }).create();
  }

  /** 출역 이력 페이지 조회 API */
  async getEntryPage(
    params: ReqEntrySearch
  ): Promise<BaseApiResponse<EntryMoCountDtoGroup, EntryListDto>> {
    return await this.AxiosV2.get<BaseApiResponse<EntryMoCountDtoGroup, EntryListDto>>('/entry', {
      params
    }).then((res) => res.data);
  }

  /** 출입 엔트리 배치 등록 API */
  async enrollBatchEntry(params: ReqEntryBatchInsert): Promise<BaseApiResponse<EntryDetailDto>> {
    return await this.Axios.post<BaseApiResponse<EntryDetailDto>>('/entry', params).then(
      (res) => res.data
    );
  }

  /** 출입 엔트리 관리 API */
  async manageEntry(mac: string, params: EntryTypeDto): Promise<BaseApiResponse<EntryDetailDto>> {
    return await this.Axios.post<BaseApiResponse<EntryDetailDto>>(`/entry/${mac}`, params).then(
      (res) => res.data
    );
  }

  /** 출입 인원 수 조회 API */
  async getAttendanceCount(data: ReqAttendanceCount): Promise<BaseApiResponse> {
    return await this.Axios.get<BaseApiResponse>(`/entry/${data.entryDate}`, {
      params: data.companyId ? { companyId: data.companyId } : {}
    }).then((res) => res.data);
  }

  /** 주간 총 합계 조회 API */
  async getTotalCountWeekly(
    entryDate: string
  ): Promise<BaseApiResponse<unknown, EntryWeeklyDtoEntryWeeklyResDto>> {
    return await this.Axios.get<BaseApiResponse<unknown, EntryWeeklyDtoEntryWeeklyResDto>>(
      `/entry/${entryDate}/weekly`
    ).then((res) => res.data);
  }

  /** 사용자 개인 출역 이력 조회 API */
  async getEntryHistory(
    userId: string,
    params: Omit<ReqEntryHistorySearch, 'userId'>
  ): Promise<BaseApiResponse<EntryHistoryResDto, EntryHistoryItem>> {
    return await this.Axios.get<BaseApiResponse<EntryHistoryResDto, EntryHistoryItem>>(
      `/entry/history/${userId}`,
      { params }
    ).then((res) => res.data);
  }

  /** 작업자 ID를 통한 출입 목록 조회 API */
  async getEntryListByWorkerId(
    userId: string,
    params: ReqEntrySearch
  ): Promise<BaseApiResponse<unknown, EntryListDto>> {
    return await this.AxiosV2.get<BaseApiResponse<unknown, EntryListDto>>(
      `/entry/detail/${userId}`,
      {
        params
      }
    ).then((res) => res.data);
  }
}
